// ═══════════════════════════════════════════════════════════════════════
// BranchPnlPage — Sprint 2026-Q1d / Step ⑦ · Pass 1 · Report ②
// ═══════════════════════════════════════════════════════════════════════
// Direct-Margin P&L per Branch. Same frozen waterfall as Project P&L but
// the dimension is the Branch master instead of the Project master.
// All 5 source collections carry `branch_id` (SI, PI, IV, snapshots,
// adjustments).
//
// Waterfall shown:
//   Revenue (SI)
//     − Material (PI)
//     − Material (IV)
//     − Depreciation (snapshots + adjustments)
//   = Direct Margin · Direct Margin %
//
// Backend wiring:
//   GET /api/reports/branch-pnl/_summary
//   GET /api/reports/branch-pnl/{id}/drill/{src}
//   GET /api/reports/branch-pnl/_reconcile
//   GET /api/reports/branch-pnl/export.csv
//
// Drill-down: clicking a cost cell opens a tab listing the underlying
// vouchers / snapshots / adjustments that aggregate into that figure.
// ═══════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import { ViewOverlay } from "@/hooks/useViewOverlay";
import { useFormTab } from "@/hooks/useFormTab";
import {
  Filter as FilterIcon, RefreshCw, Download, Search, X,
  CheckCircle2, AlertTriangle, ArrowRight, FileText,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import UserLabel, { useUserDirectory } from "@/components/UserLabel";

const currentFy = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth();
  const startYear = m >= 3 ? y : y - 1;
  return {
    from_period: `${startYear}-04`,
    to_period:   `${(startYear + 1).toString().padStart(4, "0")}-03`,
  };
};

const COST_SOURCES = [
  { key: "revenue",                   label: "Revenue (Sales Invoices)",       tone: "emerald" },
  { key: "material_pi",               label: "Material — Purchases (PI)",       tone: "amber"   },
  { key: "material_iv",               label: "Material — Stock Issues (IV)",    tone: "amber"   },
  { key: "material_pmc",              label: "Material — Project Consumption (PMC)", tone: "amber" },
  { key: "depreciation_snapshots",    label: "Depreciation Snapshots",          tone: "slate"   },
  { key: "depreciation_adjustments",  label: "Depreciation Adjustments",        tone: "slate"   },
];


export default function BranchPnlPage() {
  useUserDirectory();
  const openFormTab = useFormTab();
  const [{ from_period, to_period }, setRange] = useState(currentFy());
  const [includeInactive, setIncludeInactive] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [reconcile, setReconcile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, rec] = await Promise.all([
        api.get("/reports/branch-pnl/_summary", {
          params: { from_period, to_period, include_inactive: includeInactive },
        }),
        api.get("/reports/branch-pnl/_reconcile", {
          params: { from_period, to_period },
        }),
      ]);
      setRows(res.data?.rows || []);
      setTotals(res.data?.totals || {});
      setReconcile(rec.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Branch P&L.");
    } finally {
      setLoading(false);
    }
  }, [from_period, to_period, includeInactive]);

  useEffect(() => { load(); }, [load]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      (r.branch_code || "").toLowerCase().includes(q) ||
      (r.branch_name || "").toLowerCase().includes(q) ||
      (r.city        || "").toLowerCase().includes(q) ||
      (r.state       || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const openDrill = (row, source) => {
    const meta = COST_SOURCES.find(s => s.key === source);
    openFormTab({
      path: `/reports/branch-pnl/drill/${row.branch_id}/${source}`,
      label: `${meta?.label || source} · ${row.branch_code || row.branch_name || ""}`,
      parent: "/reports/branch-pnl",
      render: ({ close }) => (
        <BranchPnlDrillTab
          row={row}
          source={source}
          from_period={from_period}
          to_period={to_period}
          onClose={close}
        />
      ),
    });
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams({
        from_period, to_period,
        include_inactive: String(includeInactive),
      });
      const resp = await api.get(`/reports/branch-pnl/export.csv?${params}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `branch-pnl-${from_period}_${to_period}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch {
      toast.error("Failed to export CSV.");
    }
  };

  const reconcilePass = reconcile?.checks?.every(c => c.status === "PASS");

  return (
    <div className="space-y-6 p-6" data-testid="branch-pnl-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Step ⑦ · Pass 1 · Report ② · Direct Margin"
        title="Branch P&L"
        subtitle="Revenue · Direct Costs · Direct Margin per branch. Operational view — overhead allocation arrives in Pass 3 (after MA Stage 3)."
        actions={
          <div className="flex items-center gap-2">
            <button
              data-testid="branch-pnl-refresh-btn"
              onClick={load}
              disabled={loading}
              className="text-xs uppercase tracking-wider font-bold px-3 py-2 border border-slate-300 hover:bg-slate-50"
              title="Reload data">
              <RefreshCw size={12} className={`inline mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              data-testid="branch-pnl-export-csv-btn"
              onClick={exportCsv}
              className="text-xs uppercase tracking-wider font-bold px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white">
              <Download size={12} className="inline mr-1" /> Export CSV
            </button>
          </div>
        }
      />

      {/* Filter bar */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <FilterIcon size={14} className="text-slate-500" />
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Filters</div>
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
          <FilterField label="Period From">
            <input
              data-testid="branch-pnl-filter-from"
              type="month"
              value={from_period}
              onChange={e => setRange(r => ({ ...r, from_period: e.target.value }))}
              className={inputCls}
            />
          </FilterField>
          <FilterField label="Period To">
            <input
              data-testid="branch-pnl-filter-to"
              type="month"
              value={to_period}
              onChange={e => setRange(r => ({ ...r, to_period: e.target.value }))}
              className={inputCls}
            />
          </FilterField>
          <FilterField label="Visibility">
            <label className="flex items-center gap-2 text-xs h-[34px]">
              <input
                data-testid="branch-pnl-filter-include-inactive"
                type="checkbox"
                checked={includeInactive}
                onChange={e => setIncludeInactive(e.target.checked)}
                className="h-4 w-4"
              />
              Include inactive branches
            </label>
          </FilterField>
        </div>
      </Card>

      {/* KPI tiles */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Revenue"          value={totals.revenue}         tone="emerald" />
        <KpiTile label="Total Direct Costs" value={totals.direct_costs}  tone="amber" />
        <KpiTile label="Direct Margin"    value={totals.direct_margin}    tone={totals.direct_margin >= 0 ? "emerald-bold" : "rose"} />
        <KpiTile label="Direct Margin %"  value={totals.direct_margin_pct} suffix="%" tone="slate" />
      </div>

      {/* Reconciliation badge */}
      {reconcile && (
        <Card className={`p-4 border ${reconcilePass ? "border-emerald-300 bg-emerald-50/40" : "border-amber-300 bg-amber-50/40"}`}>
          <div className="flex items-start gap-3" data-testid="branch-pnl-reconcile-banner">
            {reconcilePass
              ? <CheckCircle2 size={18} className="text-emerald-700 flex-none mt-0.5" />
              : <AlertTriangle size={18} className="text-amber-700 flex-none mt-0.5" />}
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-700">
                Reconciliation — Rolled-up vs Source (Step ⑧ pre-cursor)
              </div>
              <div className="font-display text-base font-black text-slate-900 mt-0.5">
                {reconcilePass
                  ? "All 4 checks PASS — Branch P&L ties exactly to source data."
                  : `${reconcile.checks.filter(c => c.status === "FAIL").length} of ${reconcile.checks.length} check(s) failing.`}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-2">
                {reconcile.checks.map((c, i) => (
                  <div key={i}
                       data-testid={`branch-pnl-reconcile-${c.check.toLowerCase().replace(/[^a-z]/g, "-")}`}
                       className={`text-xs px-2 py-1 rounded ${c.status === "PASS"
                                    ? "bg-emerald-100 text-emerald-900"
                                    : "bg-amber-100 text-amber-900"}`}>
                    <div className="font-bold">{c.status} · {c.check}</div>
                    {c.status === "FAIL" && (
                      <div className="text-[10px] mt-0.5">
                        Δ ₹{c.delta.toLocaleString("en-IN")} (rolled: ₹{c.rolled_up.toLocaleString("en-IN")} · source: ₹{c.source.toLocaleString("en-IN")})
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {!reconcilePass && (
                <div className="text-[11px] text-slate-600 mt-2 italic">
                  Drift here is usually orphan vouchers whose branch_id no longer exists. The full 5-check Financial Integrity Dashboard (Step ⑧) surfaces drill-down to fix.
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="branch-pnl-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search branch code / name / city / state…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 focus:border-[#F97316] focus:outline-none"
          />
        </div>
        <div className="text-xs text-slate-500">
          {filteredRows.length} of {rows.length} branches
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <Card className="p-12 text-center text-slate-500">Loading Branch P&L…</Card>
      ) : rows.length === 0 ? (
        <EmptyState title="No branches in this view"
                    hint="Try toggling 'Include inactive branches' or widen the period." />
      ) : filteredRows.length === 0 ? (
        <EmptyState title="No matches" hint="Clear the search to see all branches." />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="branch-pnl-table">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>
                  {["Branch", "Location", "Status",
                    "Revenue", "Material (PI)", "Material (IV)", "Material (PMC)", "Depreciation",
                    "Direct Costs", "Direct Margin", "Margin %"].map(h =>
                      <th key={h}
                          className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-3">
                        {h}
                      </th>
                    )}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(r => (
                  <tr key={r.branch_id}
                      data-testid={`branch-pnl-row-${r.branch_id}`}
                      className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3">
                      <div className="font-mono font-bold text-[#F97316]">{r.branch_code || "—"}</div>
                      <div className="text-xs text-slate-600 truncate max-w-[280px]">{r.branch_name}</div>
                    </td>
                    <td className="py-2 px-3 text-xs text-slate-700">
                      {[r.city, r.state].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td className="py-2 px-3 text-xs">
                      <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${
                        r.is_active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"
                      }`}>{r.is_active ? "Active" : "Inactive"}</span>
                    </td>
                    <DrillCell value={r.revenue}      onClick={() => r._counts.revenue && openDrill(r, "revenue")} count={r._counts.revenue} tone="emerald" />
                    <DrillCell value={r.material_pi}  onClick={() => r._counts.material_pi && openDrill(r, "material_pi")} count={r._counts.material_pi} tone="amber" prefix="-" />
                    <DrillCell value={r.material_iv}  onClick={() => r._counts.material_iv && openDrill(r, "material_iv")} count={r._counts.material_iv} tone="amber" prefix="-" />
                    <DrillCell value={r.material_pmc_net} onClick={() => r._counts.material_pmc && openDrill(r, "material_pmc")} count={r._counts.material_pmc} tone="amber" prefix="-" />
                    <DrillCell value={r.depreciation} onClick={() => r._counts.snapshots && openDrill(r, "depreciation_snapshots")} count={r._counts.snapshots + r._counts.adjustments} tone="slate" prefix="-" />
                    <td className="py-2 px-3 text-right tabular-nums text-xs text-amber-800 font-bold">
                      {r.direct_costs ? `-₹${r.direct_costs.toLocaleString("en-IN")}` : "—"}
                    </td>
                    <td className={`py-2 px-3 text-right tabular-nums text-sm font-black ${
                      r.direct_margin > 0 ? "text-emerald-700" :
                      r.direct_margin < 0 ? "text-rose-700" : "text-slate-400"
                    }`}>
                      {r.revenue || r.direct_costs ? (r.direct_margin >= 0 ? "₹" : "-₹") + Math.abs(r.direct_margin).toLocaleString("en-IN") : "—"}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-xs font-bold">
                      {r.direct_margin_pct === null
                        ? <span className="text-slate-300">—</span>
                        : <span className={r.direct_margin_pct >= 0 ? "text-emerald-700" : "text-rose-700"}>
                            {r.direct_margin_pct}%
                          </span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-100 border-t-2 border-slate-300 font-black">
                <tr data-testid="branch-pnl-totals-row">
                  <td className="py-3 px-3 text-xs uppercase tracking-wider">Totals ({totals.branch_count})</td>
                  <td colSpan={2}></td>
                  <td className="py-3 px-3 text-right tabular-nums text-emerald-800">₹{(totals.revenue || 0).toLocaleString("en-IN")}</td>
                  <td className="py-3 px-3 text-right tabular-nums text-amber-800">-₹{(totals.material_pi || 0).toLocaleString("en-IN")}</td>
                  <td className="py-3 px-3 text-right tabular-nums text-amber-800">-₹{(totals.material_iv || 0).toLocaleString("en-IN")}</td>
                  <td className="py-3 px-3 text-right tabular-nums text-amber-800">-₹{(totals.material_pmc || 0).toLocaleString("en-IN")}</td>
                  <td className="py-3 px-3 text-right tabular-nums text-amber-800">-₹{(totals.depreciation || 0).toLocaleString("en-IN")}</td>
                  <td className="py-3 px-3 text-right tabular-nums text-amber-800">-₹{(totals.direct_costs || 0).toLocaleString("en-IN")}</td>
                  <td className={`py-3 px-3 text-right tabular-nums text-sm ${(totals.direct_margin || 0) >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {(totals.direct_margin || 0) >= 0 ? "₹" : "-₹"}{Math.abs(totals.direct_margin || 0).toLocaleString("en-IN")}
                  </td>
                  <td className={`py-3 px-3 text-right tabular-nums text-xs ${(totals.direct_margin_pct || 0) >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {totals.direct_margin_pct ?? "—"}{totals.direct_margin_pct !== null ? "%" : ""}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}

      {/* Data Source footer (per D6 standard) */}
      <Card className="p-4 bg-slate-50">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-bold mb-2">
          <FileText size={11} className="inline mr-1" /> Data Sources (Pass 1)
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-1 text-xs text-slate-700">
          <div>• Sales Invoices <span className="text-slate-400">(status ∈ Approved, Posted)</span></div>
          <div>• Purchase Invoices <span className="text-slate-400">(status ∈ Approved, Posted)</span></div>
          <div>• Issue Vouchers <span className="text-slate-400">(status = Approved)</span></div>
          <div>• Depreciation Snapshots <span className="text-slate-400">(immutable Step ⑤)</span></div>
          <div>• Depreciation Adjustments <span className="text-slate-400">(append-only Step ⑥)</span></div>
          <div className="text-slate-400">• Management Accounting Allocations <span className="italic">(Pass 3)</span></div>
        </div>
      </Card>
    </div>
  );
}


// ─── Drill-down tab ─────────────────────────────────────────────────────────

function BranchPnlDrillTab({ row, source, from_period, to_period, onClose }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const meta = COST_SOURCES.find(s => s.key === source) || { label: source };

  useEffect(() => {
    setLoading(true);
    api.get(`/reports/branch-pnl/${row.branch_id}/drill/${source}`, {
      params: { from_period, to_period, limit: 500 },
    })
      .then(r => setData(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load drill-down."))
      .finally(() => setLoading(false));
  }, [row.branch_id, source, from_period, to_period]);

  return (
    <ViewOverlay onClose={onClose} testid="branch-pnl-drill-overlay">
      <Toaster richColors position="top-right" />
      <div className="p-6 space-y-4">
        <PageHeader
          eyebrow={`Drill-Down · ${row.branch_code}`}
          title={meta.label}
          subtitle={`${row.branch_name} · ${[row.city, row.state].filter(Boolean).join(", ")} · ${from_period} → ${to_period}`}
          actions={
            <button onClick={onClose}
                    className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900">
              <X size={16} className="inline mr-1" /> Close
            </button>
          }
        />
        {loading
          ? <div className="text-center text-slate-500 py-12">Loading source documents…</div>
          : data.length === 0
          ? <EmptyState title="No source documents in this scope" hint="Check the period range." />
          : <Card className="overflow-x-auto">
              <DrillTable source={source} rows={data} />
            </Card>}
      </div>
    </ViewOverlay>
  );
}


function DrillTable({ source, rows }) {
  if (source === "revenue") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-revenue">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">Invoice #</th><th className="text-left p-3">Date</th><th className="text-left p-3">Client</th><th className="text-left p-3">Status</th><th className="text-left p-3">Payment</th><th className="text-right p-3">Total</th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="p-3 font-mono font-bold text-[#F97316]">{r.invoice_number}</td>
              <td className="p-3 text-xs">{r.invoice_date}</td>
              <td className="p-3 text-xs">{r.client_name || "—"}</td>
              <td className="p-3 text-xs">{r.status}</td>
              <td className="p-3 text-xs">{r.payment_status || "—"}</td>
              <td className="p-3 text-right tabular-nums font-bold">₹{(r.total_amount || 0).toLocaleString("en-IN")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (source === "material_pi") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-pi">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">PI #</th><th className="text-left p-3">Date</th><th className="text-left p-3">Vendor</th><th className="text-left p-3">Status</th><th className="text-right p-3">Total</th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="p-3 font-mono font-bold text-[#F97316]">{r.invoice_number}</td>
              <td className="p-3 text-xs">{r.invoice_date}</td>
              <td className="p-3 text-xs">{r.vendor_name || "—"}</td>
              <td className="p-3 text-xs">{r.status}</td>
              <td className="p-3 text-right tabular-nums font-bold text-amber-800">-₹{(r.total_amount || 0).toLocaleString("en-IN")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (source === "material_iv") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-iv">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">Voucher #</th><th className="text-left p-3">Date</th><th className="text-left p-3">Status</th><th className="text-right p-3">Total</th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="p-3 font-mono font-bold text-[#F97316]">{r.voucher_number}</td>
              <td className="p-3 text-xs">{r.voucher_date}</td>
              <td className="p-3 text-xs">{r.status}</td>
              <td className="p-3 text-right tabular-nums font-bold text-amber-800">-₹{(r.total_amount || 0).toLocaleString("en-IN")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (source === "material_pmc") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-pmc">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">PMC #</th><th className="text-left p-3">Date</th><th className="text-left p-3">Item</th><th className="text-right p-3">Qty</th><th className="text-right p-3">Rate (Prov.)</th><th className="text-left p-3">Status</th><th className="text-right p-3">Value</th></tr>
        </thead>
        <tbody>
          {rows.flatMap(r => (r.line_items || []).map((ln, i) => (
            <tr key={`${r.id}-${i}`} className="border-b border-slate-100">
              <td className="p-3 font-mono font-bold text-[#F97316]">{r.consumption_number}</td>
              <td className="p-3 text-xs">{r.consumption_date}</td>
              <td className="p-3 text-xs">{ln.item_name}</td>
              <td className="p-3 text-right tabular-nums text-xs">{ln.consumed_qty} {ln.uom}</td>
              <td className="p-3 text-right tabular-nums text-xs">₹{Number(ln.provisional_unit_rate || 0).toLocaleString("en-IN")}</td>
              <td className="p-3 text-xs">
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${
                  r.status === "Reversed" ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"
                }`}>{r.status === "Reversed" ? "Reversed (net ₹0)" : r.status}</span>
              </td>
              <td className="p-3 text-right tabular-nums font-bold text-amber-800">-₹{Number(ln.provisional_value || 0).toLocaleString("en-IN")}</td>
            </tr>
          )))}
        </tbody>
      </table>
    );
  }
  if (source === "depreciation_snapshots") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-snap">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">Period</th><th className="text-left p-3">Asset</th><th className="text-left p-3">Method</th><th className="text-right p-3">Opening NBV</th><th className="text-right p-3">Depreciation</th><th className="text-right p-3">Closing NBV</th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="p-3 font-mono font-bold">{r.period_key}</td>
              <td className="p-3 text-xs">
                <span className="font-mono font-bold text-[#F97316]">{r.asset_code}</span>
                <span className="text-slate-400 ml-1">· {r.asset_name}</span>
              </td>
              <td className="p-3 text-xs">{r.method}</td>
              <td className="p-3 text-right tabular-nums">₹{(r.opening_nbv || 0).toLocaleString("en-IN")}</td>
              <td className="p-3 text-right tabular-nums font-bold text-amber-800">-₹{(r.monthly_depreciation || 0).toLocaleString("en-IN")}</td>
              <td className="p-3 text-right tabular-nums">₹{(r.closing_nbv || 0).toLocaleString("en-IN")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (source === "depreciation_adjustments") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-adj">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">Period</th><th className="text-left p-3">Asset</th><th className="text-right p-3">Δ Depreciation</th><th className="text-left p-3">Reason</th><th className="text-left p-3">By</th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="p-3 font-mono font-bold">{r.period_key}</td>
              <td className="p-3 text-xs">{r.asset_code || "—"}</td>
              <td className={`p-3 text-right tabular-nums font-bold ${r.delta >= 0 ? "text-rose-700" : "text-emerald-700"}`}>
                {r.delta >= 0 ? "+" : ""}{formatINR(r.delta)}
              </td>
              <td className="p-3 text-xs max-w-md truncate" title={r.reason}>{r.reason}</td>
              <td className="p-3 text-[10px] text-slate-500"><UserLabel value={r.posted_by} fallback="—" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return null;
}


// ─── Small helpers ──────────────────────────────────────────────────────────

const inputCls = "w-full border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#F97316]";

function FilterField({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function KpiTile({ label, value, tone = "slate", suffix = "" }) {
  const palettes = {
    "emerald":      "bg-emerald-50 border-emerald-200 text-emerald-900",
    "emerald-bold": "bg-emerald-100 border-emerald-300 text-emerald-900",
    "amber":        "bg-amber-50 border-amber-200 text-amber-900",
    "rose":         "bg-rose-50 border-rose-200 text-rose-900",
    "slate":        "bg-slate-50 border-slate-200 text-slate-900",
  };
  const v = (value === null || value === undefined)
    ? "—"
    : (suffix ? `${value}${suffix}` : `₹${Number(value).toLocaleString("en-IN")}`);
  return (
    <div className={`p-4 border ${palettes[tone]}`} data-testid={`branch-pnl-kpi-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold opacity-70">{label}</div>
      <div className="text-xl font-black tabular-nums mt-1">{v}</div>
    </div>
  );
}

function DrillCell({ value, onClick, count, tone = "slate", prefix = "" }) {
  const disabled = !count || count === 0;
  const palette = {
    "emerald": "text-emerald-700",
    "amber":   "text-amber-800",
    "slate":   "text-slate-700",
  }[tone] || "text-slate-700";
  return (
    <td className={`py-2 px-3 text-right tabular-nums text-xs ${palette} ${disabled ? "" : "cursor-pointer hover:underline"}`}
        onClick={disabled ? undefined : onClick}
        data-testid={disabled ? undefined : `drill-cell-${tone}`}
        title={disabled ? "No documents in this scope" : `Click to see ${count} document(s)`}>
      {value
        ? <>{prefix}₹{Number(value).toLocaleString("en-IN")}{!disabled && <ArrowRight size={9} className="inline ml-1 opacity-40" />}</>
        : <span className="text-slate-300">—</span>}
    </td>
  );
}
